import { useCallback, useState } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import {
	insertFurnitureIntoCache,
	reorderFurnitureInCache,
	spliceErefIntoCache,
} from "helpers/quotationCache.helper"
import AnnotationService from "services/annotations.service"
import type { useCreateFurniture } from "hooks/api/quotation/quotations.hook"
import type { TCreateFurnitureResponse, TFurniture, TFurnitureCustomization } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import type { TPaneBinding } from "../core/viewerPanes"
import type { TViewerTool } from "../core/viewerTools"
import { useAreaCreate, type TAreaCreateSession, type TAreaDraft } from "./useAreaCreate"
import { useApplyCustomization } from "./useApplyCustomization"

export type TFurnitureDraftArea = TAreaDraft
/** Live "add object" session — the shared area-create session under its own name. */
export type TFurnitureCreateSession = TAreaCreateSession

/** What the create panel has collected by the time Create object is pressed. */
export type TFurnitureCreateDetails = {
	/** Leaf ("Object") category id. Null only for an "Other" item, which the cabinet flow excludes. */
	relatedCategoryId: number | null
	width: number | null
	height: number | null
	depth: number | null
	selectedModelNumberId: number | null
	/** The elevation reference to link to, at the version the cache last saw. Optional (REQ-293). */
	eref: { id: number; version: number } | null
	/**
	 * REQ-413/414. Null/absent unless the panel's Customization section is expanded AND complete —
	 * the customization endpoint is per-object, so it can only be applied after the create.
	 */
	customization?: TFurnitureCustomization | null
}

export type FurnitureCreateContext = {
	/** Orchestrator-owned mirror of the live session (see useAreaCreate). */
	createSessionRef: MutableRefObject<TFurnitureCreateSession | null>
	/** Plain (non-React) draft box + its four corner handles, from createOverlayContainers. */
	erefDraftRectRef: MutableRefObject<{ element: HTMLElement; handles: HTMLElement[] } | null>
	/** `.App` element — the origin the box's app-relative coordinates are measured against. */
	appContainerRef: MutableRefObject<HTMLElement | null>
	/** The live viewer panes, re-resolved on every call (they are replaced on a layout change). */
	getViewerPanes: () => TPaneBinding[]
	/** Bumped by split-screen enter/exit; re-binds the canvas listeners onto the new panes. */
	layoutEpoch: number
	numericQuotationIdRef: MutableRefObject<number | undefined>
	createFurnitureAsync: ReturnType<typeof useCreateFurniture>["mutateAsync"]
	queryClient: QueryClient
	/** Post-create hand-off to the orchestrator: select the new object and open its sidebar. */
	onFurnitureCreated: (furniture: TFurniture) => void
	/** The shared tool command (useCanvasTools) — the mode puts every pane back into select. */
	onToolChange: (tool: TViewerTool) => void
	clearSelectionToolbar: () => void
	success: (text: string) => void
	formatError: (error: unknown) => void
}

/**
 * "Add cabinet" mode (REQ-293), opened from the header's + Add → Cabinet menu. It is the elevation-
 * reference flow one level down: the same shared `useAreaCreate` gesture defines the object's area,
 * and this hook owns the create request — `POST /quotations/{id}/furniture/` with the area plus the
 * details the panel collected (category, dimensions, model number, optional elevation reference).
 *
 * The response is applied straight to the cache and the canvas, exactly as the duplicate/paste flow
 * does (useFurnitureClipboard): insert, then apply the backend-decided group order, then splice the
 * eref back at its bumped version. `insertFurnitureIntoCache` no-ops when no cached page matches the
 * new object's page, and drawing the highlight anyway would leave a canvas-only object that vanishes
 * on the next full re-render — so that case refetches instead.
 */
export const useFurnitureCreate = (ctx: FurnitureCreateContext) => {
	const {
		createSessionRef,
		erefDraftRectRef,
		appContainerRef,
		getViewerPanes,
		layoutEpoch,
		numericQuotationIdRef,
		createFurnitureAsync,
		queryClient,
		onFurnitureCreated,
		onToolChange,
		clearSelectionToolbar,
		success,
		formatError,
	} = ctx

	const [isCreatePending, setIsCreatePending] = useState(false)
	const applyCustomization = useApplyCustomization(formatError)

	const {
		createSession,
		startCreateArea: startCreateFurniture,
		cancelCreateArea: cancelCreateFurniture,
	} = useAreaCreate({
		createSessionRef,
		draftRectRef: erefDraftRectRef,
		appContainerRef,
		getViewerPanes,
		layoutEpoch,
		onToolChange,
		clearSelectionToolbar,
	})

	const runCreateFurniture = useCallback(
		async (details: TFurnitureCreateDetails) => {
			const qid = numericQuotationIdRef.current
			const area = createSessionRef.current?.area
			if (qid == null || Number.isNaN(qid) || !area) return
			setIsCreatePending(true)
			try {
				const response = await createFurnitureAsync({
					quotationId: qid,
					body: {
						page_number: area.pageNumber,
						x0: area.coordinates.x0,
						y0: area.coordinates.y0,
						x1: area.coordinates.x1,
						y1: area.coordinates.y1,
						related_category_id: details.relatedCategoryId,
						// Dimensions and the model number are optional to the endpoint; omit rather than
						// send null, so an empty field is "not set" instead of an explicit clear.
						...(details.width !== null ? { width: details.width } : {}),
						...(details.height !== null ? { height: details.height } : {}),
						...(details.depth !== null ? { depth: details.depth } : {}),
						...(details.selectedModelNumberId !== null
							? { selected_model_number_id: details.selectedModelNumberId }
							: {}),
						// Sent as a pair or not at all — the endpoint rejects a version without an id and
						// uses the version for the eref's optimistic lock.
						...(details.eref ? { eref_id: details.eref.id, eref_version: details.eref.version } : {}),
					},
				})
				const data = response?.data as TCreateFurnitureResponse | undefined
				const createdFurniture = data?.this_furniture
				if (!createdFurniture) return

				// The one extra call the customization costs (REQ-413/414), against the version the
				// create returned. Best-effort: past this line the object exists no matter how it went.
				const created = await applyCustomization(qid, createdFurniture, details.customization ?? null)

				let isInCache = false
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) => {
					// Insert first (the ordering references the new id), then apply the backend-decided
					// group order so the object lands in the sidebar where the backend put it — not just
					// appended last. Splice the eref last so its bumped version is reflected too.
					let next = insertFurnitureIntoCache(cached, created)
					if (next !== cached) isInCache = true
					if (data?.ordering) next = reorderFurnitureInCache(next, data.ordering)
					return data?.updated_eref ? spliceErefIntoCache(next, data.updated_eref) : next
				})

				// No cached page to hold the object, so the splice above was a no-op: refetch instead of
				// drawing a canvas-only highlight that the next full re-render would drop.
				if (!isInCache) queryClient.invalidateQueries({ queryKey: ["quotation", qid] })

				// Tear the mode down BEFORE handing over: the hand-off opens the new object's own
				// sidebar, which would otherwise be fighting the create panel for the right panel.
				cancelCreateFurniture()

				// The hand-off runs on BOTH paths, and that is the point: `cancelCreateFurniture` is the
				// area-draw session cancel, which clears the session and nothing else. Only the hand-off
				// moves the right panel off "create-furniture" (via handleFurnitureClick). Returning
				// early here — as this did — left the panel in a mode whose session was gone, and since
				// both render branches are guarded on the pair, the user was left staring at a blank
				// right panel with no way back.
				if (isInCache) AnnotationService.addFurnitureAnnotation(created)
				onFurnitureCreated(created)
				if (isInCache) AnnotationService.selectFurniture(created.id)
				success("Object created")
			} catch (e) {
				// The area and the panel stay exactly as they were so the user can fix whatever the
				// backend rejected (duplicate coordinates, a model number that doesn't match the
				// category, a stale eref version) and retry.
				formatError(e)
			} finally {
				setIsCreatePending(false)
			}
		},
		[
			numericQuotationIdRef,
			createSessionRef,
			createFurnitureAsync,
			applyCustomization,
			queryClient,
			cancelCreateFurniture,
			onFurnitureCreated,
			success,
			formatError,
		],
	)

	return {
		createSession,
		isCreatePending,
		startCreateFurniture,
		cancelCreateFurniture,
		runCreateFurniture,
	}
}
